import React from 'react';
import { Link } from 'react-router-dom';
import useSeo from './useSeo';
import { SQUADRONS, getSquadron } from '../data/squadrons';
import { allQuals, trainingSchedule } from '../data/squadron-knowledge';
import { trackJoinDiscord } from '../lib/analytics';

const SITE = 'https://jointtaskforceheavy.com';
const DISCORD = 'https://discord.gg/sPavY3ZZQH';

const H2 = ({ children }) => (
  <h2 className="font-heading text-3xl lg:text-4xl font-bold tracking-tight uppercase text-hvy-900 mt-14 mb-4">
    {children}
    <span className="block mt-3 h-1 w-16 bg-hvy-accent rounded-full" />
  </h2>
);

// FAQ content lives in one place: rendered visibly below AND serialized into
// FAQPage structured data. Google requires the two to match exactly, so they
// must never be maintained separately.
const FAQ = [
  {
    q: 'Do I need experience to join a DCS squadron?',
    a: 'No. Our squadrons recruit on attendance rather than skill, and members teach the airframe, the tactics and the radio work from the ground up. Several squadrons run formal instructor tracks specifically to bring new pilots up to speed.',
  },
  {
    q: 'How much time do I have to commit?',
    a: 'It varies by squadron and each one sets its own expectation. The 472nd, for example, asks recruits to attend two trainings or missions a month. Others simply expect you to show up to the flight nights you signed up for.',
  },
  {
    q: 'Which aircraft can I fly?',
    a: 'Across the task force: the F-16C Viper, F/A-18C Hornet, A-10C II Warthog, F-14B Tomcat, F-4E Phantom II, C-130J Hercules, rotary wing including the Apache and Kiowa, plus dedicated adversary air and civil aviation.',
  },
  {
    q: 'Is there an age requirement?',
    a: 'Yes. Joint Task Force Heavy is an 18+ community, which keeps the level of engagement and professionalism we expect.',
  },
  {
    q: 'Can I be in more than one DCS group at the same time?',
    a: 'No. We ask members to commit fully to HVY and not to fly with other DCS groups at the same time.',
  },
  {
    q: 'Do I have to choose a squadron straight away?',
    a: 'No. Join the Discord first and talk to us. We will point you at the squadrons flying the aircraft you want, on the nights you are actually online, before you commit to one.',
  },
];

const JoinPage = () => {
  const quals = allQuals();
  const schedule = trainingSchedule();

  useSeo({
    title: 'How to Join a DCS Squadron: A Beginner\'s Guide | JTF Heavy',
    description:
      'How to find and join a DCS World squadron, step by step. Compare 11 recruiting squadrons, real training nights and qualification tracks. No prior jet experience required, 18+.',
    canonical: `${SITE}/join-dcs-squadron`,
  });

  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE}/` },
          { '@type': 'ListItem', position: 2, name: 'How to Join a DCS Squadron', item: `${SITE}/join-dcs-squadron` },
        ],
      },
      {
        '@type': 'FAQPage',
        mainEntity: FAQ.map((f) => ({
          '@type': 'Question',
          name: f.q,
          acceptedAnswer: { '@type': 'Answer', text: f.a },
        })),
      },
    ],
  };

  return (
    <div>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />

      {/* Hero band */}
      <section className="relative bg-hvy-900 pt-28 pb-16 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <img src="/logo.webp" alt="JTF Heavy" className="mx-auto h-20 mb-6" />
          <h1 className="font-heading text-4xl md:text-5xl font-bold uppercase tracking-wider text-white">
            How to Join a DCS Squadron
          </h1>
          <p className="mt-4 text-lg text-gray-300 max-w-2xl mx-auto">
            A practical guide to finding a DCS World group to fly with, and how to
            join Joint Task Force Heavy.
          </p>
        </div>
      </section>

      {/* Content */}
      <main className="w-full px-6 py-12 bg-gray-50 bg-[url('bg.webp')] bg-repeat bg-fixed bg-top">
        <article className="max-w-3xl mx-auto text-gray-700 text-lg leading-relaxed">
          <H2>Why fly with a DCS squadron?</H2>
          <p>
            Digital Combat Simulator (DCS World) is at its best when you fly with
            other people. Joining a DCS squadron, sometimes called a group, clan,
            wing, or virtual squadron, gives you wingmen to fly with, structured
            training to sharpen your skills, organized multiplayer operations, and a
            community that actually knows your callsign. Whether you fly the F-16,
            F/A-18, A-10, F-14, or rotary wing, a good group turns solo sorties into
            coordinated missions.
          </p>

          <H2>What to look for in a DCS group to fly with</H2>
          <p>Not every squadron is a fit for every pilot. When you are choosing a DCS group, look for:</p>
          <ul className="list-disc pl-6 mt-4 space-y-2">
            <li><strong>Skill level:</strong> a group that welcomes new pilots and is willing to train, not just recruit veterans.</li>
            <li><strong>Time zone:</strong> an active community that flies when you are online.</li>
            <li><strong>Airframes:</strong> squadrons operating the aircraft you actually want to master.</li>
            <li><strong>Milsim intensity:</strong> the right balance of realism and fun for how you like to play.</li>
            <li><strong>Culture:</strong> a mature, drama-free community with clear, enforced policies.</li>
            <li><strong>Activity:</strong> regular, scheduled flight nights and campaigns, not a dead Discord.</li>
          </ul>

          <H2>Why pilots choose Joint Task Force Heavy</H2>
          <p>
            Joint Task Force Heavy (HVY) is a global DCS community with pilots from
            nearly every continent: casual gamers, commercial aviators, and former
            military pilots. We field {SQUADRONS.length} squadrons across a full range of NATO
            aircraft, organized into three battle groups plus an Auxiliary Corps, and
            we run regular squadron operations and dynamic, modern-era campaigns. No
            prior jet experience is required; our members are always eager to help new
            pilots learn their airframe. Most flight nights are scheduled around North
            American time zones (EST and PST).
          </p>

          {/* Real schedule data — the thing a prospective recruit most wants to know */}
          {schedule.length > 0 && (
            <>
              <H2>When we actually fly</H2>
              <p>
                Every squadron sets its own schedule. These are the recurring training
                nights and events our squadron leads have confirmed:
              </p>
              <div className="mt-6 overflow-x-auto rounded-xl border border-gray-200/70 shadow-sm bg-white/70 backdrop-blur-sm">
                <table className="w-full text-left text-base">
                  <thead>
                    <tr className="border-b border-gray-200 bg-white/60">
                      <th scope="col" className="px-4 py-3 font-semibold text-hvy-900 whitespace-nowrap">Squadron</th>
                      <th scope="col" className="px-4 py-3 font-semibold text-hvy-900">Event</th>
                      <th scope="col" className="px-4 py-3 font-semibold text-hvy-900 whitespace-nowrap">When</th>
                    </tr>
                  </thead>
                  <tbody>
                    {schedule.map((row, i) => {
                      const sq = getSquadron(row.slug);
                      return (
                        <tr key={`${row.slug}-${i}`} className="border-b border-gray-100 last:border-0">
                          <td className="px-4 py-3 whitespace-nowrap">
                            <Link to={`/squadrons/${row.slug}`} className="font-semibold text-hvy-500 hover:text-hvy-700 underline">
                              {sq ? sq.shortName : row.slug}
                            </Link>
                          </td>
                          <td className="px-4 py-3 text-gray-700">{row.name}</td>
                          <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{row.cadence}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <p className="mt-3 text-base text-gray-600">
                Squadrons not listed here set their flying with their members rather than on a
                fixed night. See <Link to="/squadrons" className="text-hvy-500 font-semibold hover:text-hvy-700 underline">all our squadrons</Link>{' '}
                for the full picture.
              </p>
            </>
          )}

          {/* Qualification tracks — concrete progression, aggregated from lead responses */}
          {quals.length > 0 && (
            <>
              <H2>What you can qualify for</H2>
              <p>
                Progression is real here, not cosmetic. Depending on the squadron you join,
                these are the qualifications members currently train toward:
              </p>
              <div className="mt-6 flex flex-wrap gap-2">
                {quals.map((q) => (
                  <span
                    key={q}
                    className="inline-block rounded-full border border-hvy-500/40 bg-white/70 px-3 py-1 text-base font-medium text-hvy-700"
                  >
                    {q}
                  </span>
                ))}
              </div>
            </>
          )}

          <H2>Membership requirements</H2>
          <ul className="list-disc pl-6 mt-4 space-y-2">
            <li><strong>18+ community.</strong> HVY is an adults-only group to maintain the level of engagement and professionalism we expect.</li>
            <li><strong>No multi-clanning.</strong> We ask members to commit fully and not join other DCS flying groups.</li>
            <li><strong>Zero tolerance policy.</strong> Hate speech, racism, and harassment are strictly prohibited.</li>
          </ul>

          <H2>How to join HVY in three steps</H2>
          <ol className="list-decimal pl-6 mt-4 space-y-2">
            <li>Join our Discord using the invite link below.</li>
            <li>Navigate to the <strong>#new-applications</strong> channel.</li>
            <li>Type <strong>/apply</strong> to complete your application via PirateBot.</li>
          </ol>
          <p className="mt-4">
            You do not need to have picked a squadron before you apply. Browse{' '}
            <Link to="/squadrons" className="text-hvy-500 font-semibold hover:text-hvy-700 underline">our {SQUADRONS.length} squadrons</Link>{' '}
            first if you want to, or ask us on Discord and we will help you find the fit.
          </p>

          <H2>Common questions</H2>
          <dl className="mt-4 space-y-6">
            {FAQ.map((f) => (
              <div key={f.q}>
                <dt className="font-semibold text-hvy-900">{f.q}</dt>
                <dd className="mt-1.5">{f.a}</dd>
              </div>
            ))}
          </dl>

          <div className="mt-12 text-center">
            <a
              href={DISCORD}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackJoinDiscord('join-page')}
              className="inline-block bg-discord hover:bg-discord/90 text-white font-bold py-3 px-8 rounded-lg transition-all duration-200 shadow-lg uppercase tracking-wider"
            >
              Join Our Discord
            </a>
          </div>

          <div className="mt-10 pt-8 border-t border-gray-200 text-center text-base text-gray-600">
            <p>
              Explore <Link to="/squadrons" className="text-hvy-500 font-semibold hover:text-hvy-700 underline">our DCS squadrons</Link>{' '}
              or head back to the <Link to="/" className="text-hvy-500 font-semibold hover:text-hvy-700 underline">HVY home page</Link>.
            </p>
          </div>
        </article>
      </main>
    </div>
  );
};

export default JoinPage;
